import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { EventsList } from '../../dashboard/components/EventList'
import { EventWithRelations, Event } from '../../../services/EventService'
import { AdminEventManager } from './AdminEventManager'

export interface SelectedAdminUser {
  id: string
  name: string
}

interface UserEventsPanelProps {
  user: SelectedAdminUser
}

export function UserEventsPanel({ user }: UserEventsPanelProps) {
  const [events, setEvents] = useState<EventWithRelations[]>([])
  const [helperEventIds, setHelperEventIds] = useState<Set<string>>(new Set())
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list')
  const [loading, setLoading] = useState(false)
  const [managedEventId, setManagedEventId] = useState<string | null>(null)
  const [searchParams] = useSearchParams()

  useEffect(() => {
    const load = async () => {
      if (!user?.id) return
      setLoading(true)
      setManagedEventId(null)
      try {
        // Participación como helper/attending: event_users + event_helpers
        // Attending (event_users)
        const { data: attendingUsers } = await Event.getUserEvents(user.id)
        const participating = (attendingUsers || []) as unknown as EventWithRelations[]

        // Creados por el usuario (host/main)
        const { data: created } = await Event.getByUser(user.id, true)

        // Co-host
        const { data: cohosted } = await Event.getCoHostedEvents(user.id)

        // Helpers (event_helpers)
        const { data: helpers } = await Event.getAttendingEvents(user.id)
        const helperIds = new Set((helpers || []).map(e => e.id))

        const allMap = new Map<string, EventWithRelations>()
        for (const e of participating || []) allMap.set(e.id, e)
        for (const e of created || []) allMap.set(e.id, e)
        for (const e of cohosted || []) allMap.set(e.id, e)

        setEvents(Array.from(allMap.values()))
        setHelperEventIds(helperIds)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [user?.id])

  // Reset managed view when changing view mode (list/calendar)
  useEffect(() => {
    setManagedEventId(null)
  }, [viewMode])

  // Reset managed view when changing filter (?filter=...)
  useEffect(() => {
    const filter = searchParams.get('filter') || 'all'
    // Solo usar el valor para disparar el efecto
    // Cerrar manager cuando cambia filtro
    setManagedEventId(null)
  }, [searchParams])

  const transformed = useMemo(() => {
    return (events || []).map(e => {
      const isCreator = e.created_by === user.id || e.hosts?.some(h => h.user_id === user.id && h.is_main_host)
      const isCoHost = e.hosts?.some(h => h.user_id === user.id && !h.is_main_host)
      // type mapping per app:
      // - hosted: creator or co-host (shown in Hosted tab)
      // - attending: helper (event_helpers)
      // - user: attendee (event_users)
      const type: 'hosted' | 'attending' | 'user' =
        (isCreator || isCoHost)
          ? 'hosted'
          : (helperEventIds.has(e.id) ? 'attending' : 'user')
      return {
        id: e.id,
        title: e.title,
        description: e.short_description || e.description || '',
        imageUrl: e.image_url || '',
        ecosystem: e.category || 'All ecosystem',
        type,
        date: e.start_date,
        time: e.start_time || undefined,
        location: e.location || 'Online',
        category: e.category,
        eventType: e.type,
        modality: e.modality,
        hostStatus: 'accepted' as const,
        sideEventsCount: 0,
        parentEventId: (e as any).parent_event_id,
      }
    })
  }, [events, user?.id])

  const onEventClick = (ev: any) => {
    // Si es hosted (creator o co-host), abrir manager inline; si no, ir al detalle público
    const selected = transformed.find(e => e.id === ev.id)
    if (selected?.type === 'hosted') {
      setManagedEventId(ev.id)
      return
    }
    window.location.href = `/event/${ev.id}`
  }

  return (
    <div className="border rounded-md p-3 bg-white">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-medium">Events of {user.name}</h2>
        <div className="hidden md:flex gap-2">
          <button 
            className={`border rounded-md px-2 py-1 text-sm ${viewMode === 'list' ? 'border-emerald-500' : 'border-gray-200'}`}
            onClick={() => setViewMode('list')}
          >
            List
          </button>
          <button 
            className={`border rounded-md px-2 py-1 text-sm ${viewMode === 'calendar' ? 'border-emerald-500' : 'border-gray-200'}`}
            onClick={() => setViewMode('calendar')}
          >
            Calendar
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Loading events…</div>
      ) : managedEventId ? (
        <AdminEventManager eventId={managedEventId} asUserId={user.id} />
      ) : (
        <EventsList
          events={transformed as any}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          onManageEvent={(id: string) => setManagedEventId(id)}
          onShareEvent={() => undefined}
          onEventClick={onEventClick}
          hostedCount={transformed.filter(e => e.type === 'hosted').length}
          attendingCount={transformed.filter(e => e.type === 'attending').length}
          userCount={transformed.filter(e => e.type === 'user').length}
        />
      )}
    </div>
  )
}


